<!-- eslint-disable vue/require-v-for-key -->
<script>
import Multiselect from 'vue-multiselect'
import { Helper, debounce } from '@/mixin/helper'
import { required } from 'vuelidate/lib/validators'
import { getSmsClient } from '@/services/sms'
import RestEditableTable from '@/components/RestEditableTable'

const smsAxios = getSmsClient()

const createDefaultForm = () => ({
  device: null,
  command: null,
  data: '',
  expected_response: '',
  sensor: null,
})

/**
 * Starter component
 */
export default {
  props: {
    deviceCommands: {
      type: Array,
      default: () => [],
    },
    deviceTypes: {
      type: Array,
      default: () => [],
    },
    pinCodes: {
      type: Array,
      default: () => [],
    },
    sensors: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    Multiselect,
    RestEditableTable,
  },
  mixins: [Helper],
  data() {
    return {
      selected: [],
      selectedModel: [],
      allSelected: [],
      indeterminate: null,
      filter: null,
      filterOn: [],
      sortBy: 'device_code',
      sortDesc: false,
      form: createDefaultForm(),
      isUpdateMode: false,
      selectedDeviceCommand: null,
      isSubmittingCommand: false,
      showModal: false,
      filteredItems: [],
      isLoading: false,
      selectedDevice: '',
      debounceTimer: null,
      dataOptions: {
        enabled: [],
        disabled: [],
      },
    }
  },
  validations: {
    form: {
      device: {
        device_code: { required },
      },
      data: {
        required,
      },
      sensor: {
        sensor_code: { required },
      },
      command: { required },
    },
  },
  watch: {
    'selectedDevice.deviceCode'(newVal, oldVal) {
      if (!newVal && !oldVal) return
      let self = this
      clearTimeout(self.debounceTimer)
      self.debounceTimer = setTimeout(() => {
        self.$emit('update-change', newVal)
      }, 500)
    },
    deviceCommands: {
      immediate: true,
      handler(newVal, oldVal) {
        if (!newVal || !oldVal) return
        this.$nextTick()
        this.$refs['deviceCommandTableRef'].items = this.initData()
        this.$refs['deviceCommandTableRef'].ids = this.deviceCommands?.map((e) => e.id)
      },
    },
  },
  computed: {
    /**
     * Total no. of records
     */

    title() {
      return 'Pengaturan Online Command'
    },

    fields() {
      return [
        {
          key: 'device_name',
          label: 'Kode Perangkat',
          sortable: true,
        },
        {
          key: 'device_code',
          label: 'Kode Perangkat',
          sortable: true,
        },
        {
          key: 'sensor_name',
          label: 'Nama Sensor',
          sortable: true,
        },
        {
          key: 'sensor_code',
          label: 'Kode Sensor',
          sortable: true,
          thClass: 'mc-table--70',
        },
        {
          key: 'data',
          label: 'Data',
          tdClass: 'mc-table--250',
          sortable: true,
          customColumn: true,
        },
        {
          key: 'expected_response',
          label: 'Expected Response',
          tdClass: 'mc-table--250',
          sortable: true,
          customColumn: true,
        },
        {
          key: 'command',
          label: 'Command',
          sortable: true,
        },
        {
          key: 'created_at',
          label: 'Dibuat pada',
          sortable: false,
        },
      ]
    },
    apiClient() {
      return {
        get: (param) => {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.filter = param.q ?? ''
          let response = {
            data: this.initData() ?? [],
            page: 1,
            size: this.deviceCommands?.length,
            total_count: this.deviceCommands?.length,
            total_pages: 1,
          }
          return response
        },
        delete: async (data) => {
          const _ids = data.ids ?? data
          const foundData = this.deviceCommands.filter((e) => _ids.includes(e.id))
          await this.toDelete(foundData)
          this.$emit('update-change')
        },
      }
    },
  },
  async created() {
    await this.$nextTick()
  },
  methods: {
    initData() {
      if (!this.deviceCommands) return
      this.deviceCommands.forEach((data) => {
        data.created_at = Helper.methods.momentDMHGmt7(data.created_at)
        data.can_delete = true
        data.id = `${data.device_code}-${data.sensor_code}-${data.command}`
      })
      return this.deviceCommands
    },

    getDeviceCode(device) {
      return Number(device?.deviceCode ?? device?.device_code)
    },
    getPostPayload() {
      return {
        device_code: this.getDeviceCode(this.form.device),
        sensor_code: Number(this.form.sensor?.sensor_code),
        command: this.form.command.toString(),
        data: JSON.parse(this.form.data),
        expected_response: JSON.parse(this.form.expected_response),
      }
    },
    getDeletePayload(data) {
      return {
        device_code: Number(data.device_code),
        sensor_code: Number(data.sensor_code),
        command: data.command,
      }
    },
    formatDataForInput(data) {
      return JSON.stringify(data ?? {}, null, 2)
    },
    findDeviceOption(deviceCode) {
      return this.deviceTypes.find((device) => Number(device.deviceCode ?? device.device_code) === Number(deviceCode))
    },
    findSensorOption(sensorCode) {
      return this.sensors.find((sensor) => Number(sensor.sensor_code) === Number(sensorCode))
    },
    async addDeviceCommand() {
      try {
        this.isSubmittingCommand = true
        const res = await smsAxios.post('/device-command', this.getPostPayload())
        if (!res) return
        this.$toast.success(`berhasil`)
        this.resetData()
        this.hideModal()
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isSubmittingCommand = false
      }
    },
    async updateDeviceCommand() {
      try {
        this.isSubmittingCommand = true
        const res = await smsAxios.patch('/device-command', {
          previous_value: this.getDeletePayload(this.selectedDeviceCommand),
          new_value: this.getPostPayload(),
        })
        if (!res) return
        this.$toast.success(`Data berhasil diubah`)
        this.resetData()
        this.hideModal()
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isSubmittingCommand = false
      }
    },
    submitDeviceCommand() {
      if (this.isUpdateMode) {
        this.updateDeviceCommand()
        return
      }
      this.addDeviceCommand()
    },
    /**
     * Search the table data with search input
     */
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.filteredItems = filteredItems
    },
    showModalCreate() {
      this.isUpdateMode = false
      this.selectedDeviceCommand = null
      this.form = createDefaultForm()
      this.$bvModal.show('modal-create-device')
    },
    showModalUpdate(item, index, event) {
      if (event?.target?.closest?.('button, a, input, label, .form-check')) return
      this.isUpdateMode = true
      this.selectedDeviceCommand = { ...item }
      this.form = {
        device: this.findDeviceOption(item.device_code) ?? null,
        sensor: this.findSensorOption(item.sensor_code) ?? null,
        command: item.command,
        data: this.formatDataForInput(item.data),
        expected_response: this.formatDataForInput(item.expected_response),
      }
      this.$bvModal.show('modal-create-device')
    },
    toggleAll(checked) {
      this.selectedModel = []
      this.selected = []
      if (checked) {
        for (let i = 0; i < this.filteredItems.length; i++) {
          this.selected.push(i)
          this.selectedModel.push(true)
        }
      } else {
        for (let i = 0; i < this.filteredItems.length; i++) {
          this.selectedModel.push(false)
        }
      }
    },
    checkTable(event, data) {
      if (event) {
        this.selected.push(data)
      } else {
        var myIndex = this.selected.indexOf(data)
        if (myIndex !== -1) {
          this.selected.splice(myIndex, 1)
        }
      }
      this.allSelected = this.selected.length === this.filteredItems.length ? true : false
    },

    async toDelete(data) {
      let promises = []
      for (const element of data) {
        const res = await this.deleteDeviceCommand(element)
        if (!res) continue
        promises.push()
      }

      Promise.all(promises).then(() => {
        this.$toast.success(`Data berhasil dihapus`)
        this.clearDelete()
        this.resetData()
      })
    },

    async deleteDeviceCommand(data) {
      try {
        await smsAxios.delete('/device-command', {
          data: {
            device_code: data.device_code,
            command: data.command,
            data: JSON.stringify(data.data),
            sensor_code: data.sensor_code,
          },
        })
      } catch (error) {
        this.handleError(error)
      }
    },

    clearDelete() {
      this.allSelected = false
      this.selectedModel = []
      this.selected = []
    },
    resetModal() {
      this.form = createDefaultForm()
      this.isUpdateMode = false
      this.selectedDeviceCommand = null
      this.isSubmittingCommand = false
    },
    hideModal() {
      this.$nextTick(() => {
        this.$bvModal.hide('modal-create-device')
      })
    },
    handleOk(bvModalEvent) {
      // Prevent modal from closing
      bvModalEvent.preventDefault()
      // Trigger submit handler
      this.handleSubmit()
    },
    handleError(error) {
      if (error.response) {
        const errorStatus = error.response.status
        const errorData = error.response.data

        if (errorStatus === 400 && errorData.details) {
          errorData.details.forEach((element) => {
            this.$toast.error(`${errorData.error}: ${element.field} ${element.message}`)
          })
        } else if (errorStatus === 401) {
          this.$toast.error(errorData.error)
          this.$router.push({ name: 'Login' })
        } else if (errorStatus === 403) {
          this.$toast.error(errorData.error)
        } else {
          this.$toast.error(errorData.error)
        }
      } else if (error.request) {
        this.$toast.error(error.response.statusText)
      } else {
        this.$toast.error(error.message)
      }
    },

    handleSubmit() {
      this.submitDeviceCommand()
    },
    customLabelDevice(item) {
      if (item.deviceName) return `${item.deviceName}`
    },
    searchData: debounce(function () {
      this.selectedDevice = ''
      this.$emit('update-change', '')
    }, 500),
    resetData() {
      let self = this
      clearTimeout(self.debounceTimer)
      self.debounceTimer = setTimeout(() => {
        self.$emit('update-change', this.selectedDevice?.deviceCode)
      }, 500)
    },
    handleDataAvailable(isAvailable) {
      this.isTableDataAvailable = isAvailable
    },
    async handleChange() {
      const deviceCommandTableRef = this.$refs.deviceCommandTableRef.$refs
      await this.$nextTick()
      this.fields.forEach((field) => {
        if (!field.editable) return
        deviceCommandTableRef[`${field.key}-ref`].$el.addEventListener(
          'keydown',
          Helper.methods.preventTabKey
        )
      })
    },
  },
}
</script>
<style>
.formulate-input ul.formulate-input-errors {
  font-style: normal;
  font-size: 80%;
}
.json-beautifier {
  background-color: #f5f5f5;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  overflow-x: auto;
}
</style>
<template>
  <div>
    <h4 class="card-title">{{ $t('onlineCommand.chooseDevice') }}</h4>
    <div class="align-items-baseline align-self-center d-flex justify-content-between">
      <div class="col-4">
        <b-form-group id="licensePlate_plate" label-class="required">
          <multiselect v-model="selectedDevice" :options="deviceTypes" label="deviceName" />
        </b-form-group>
      </div>
      <div class="text-end mb-3">
        <b-button
          v-if="selected.length > 0"
          @click="toDelete"
          variant="outline-danger"
          class="mx-2"
        >
          <i class="bx bx-trash-alt"></i> {{ $t('global.delete') }}
        </b-button>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <RestEditableTable
              ref="deviceCommandTableRef"
              searchable
              :fields="fields"
              :apiClient="apiClient"
              :bulkDeleteApi="true"
              :idIsString="true"
              :isEnableEditButton="false"
              @available-change="handleDataAvailable"
              trackBy="id"
              @changeData="handleChange"
              @row-clicked="showModalUpdate"
              :hidePagination="true"
              :addWithModal="showModalCreate"
              :customSearch="filter"
              :showNoData="true"
              :busy="isLoading"
              sortBy="device_code"
              :useThousandFormat="false"
            >
              <template #data="data">
                <pre class="json-beautifier">{{ JSON.stringify(data.item.data, null, 2) }}</pre>
              </template>
              <template #expected_response="data">
                <pre class="json-beautifier">{{ JSON.stringify(data.item.expected_response, null, 2) }}</pre>
              </template>
            </RestEditableTable>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      class="mc-modal"
      id="modal-create-device"
      :title="isUpdateMode ? 'Update Device Command' : 'Pengaturan Device Command'"
      size="lg"
      centered
      scrollable
      @hidden="resetModal"
      @ok="handleOk"
    >
      <form ref="form" class="mc-form" @submit.stop.prevent="handleSubmit">
        <b-row>
          <!-- Device Code -->
          <b-col md="12">
            <div class="mb-3 mc-input-info">
              <label class="mt-3 required">{{ $t('setting.form.deviceType') }}</label>
              <multiselect
                ref="deviceTypeRef"
                v-model="form.device"
                :options="deviceTypes"
                label="deviceName"
                placeholder="Tipe Perangkat"
                :allow-empty="false"
                :custom-label="customLabelDevice"
              ></multiselect>
              <div v-if="$v.form.device.device_code.$error" class="invalid-feedback">
                <span v-if="!$v.form.device.device_code.required">
                  {{ $t('setting.form.deviceType') }} {{ $t('global.validation.required') }}
                </span>
              </div>
            </div>
            <!-- End Device Code-->
            <!-- Sensor Code -->
            <div class="mb-3 mc-input-info">
              <b-form-group
                id="sensor_code"
                label="Kode Sensor"
                label-for="sensor_code"
                label-class="required"
              >
                <multiselect
                  ref="deviceTypeRef"
                  v-model="form.sensor"
                  :options="sensors"
                  placeholder="Tipe Sensor"
                  :allow-empty="false"
                  :class="{
                    'is-invalid': $v.form.sensor.sensor_code.$error,
                  }"
                  :custom-label="(e) => `${e.sensor_name} (${e.sensor_code})`"
                ></multiselect>
                <div v-if="$v.form.sensor.sensor_code.$error" class="invalid-feedback">
                  <span v-if="!$v.form.sensor.sensor_code.required">
                    {{ $t('setting.form.sensor_code') }} {{ $t('global.validation.required') }}
                  </span>
                </div>
              </b-form-group>
            </div>
            <!-- End Sensor Code -->
            <!-- Data -->
            <div class="mb-3 mc-input-info">
              <label>Data</label>
              <b-form-textarea
                id="data"
                placeholder="Write data in JSON"
                v-model="form.data"
                trim
                :class="{
                  'is-invalid': $v.form.command.$error,
                }"
                rows="8"
              ></b-form-textarea>
            </div>
            <!-- End Data -->
            <!-- Expected Response -->
            <div class="mb-3 mc-input-info">
              <label>Expected Response</label>
              <b-form-textarea
                id="expected_response"
                placeholder="Write expected response in JSON"
                v-model="form.expected_response"
                trim
                rows="8"
              ></b-form-textarea>
            </div>
            <!-- End Expected Response -->
            <!-- Command -->
            <div class="mc-input-info">
              <b-form-group id="command" label="Command" label-for="command" label-class="required">
                <b-form-input
                  id="command"
                  placeholder="Online command"
                  v-model="form.command"
                  trim
                  :class="{
                    'is-invalid': $v.form.command.$error,
                  }"
                ></b-form-input>
                <div v-if="$v.form.command.$error" class="invalid-feedback">
                  <span v-if="!$v.form.command.required">
                    {{ $t('setting.form.command') }} {{ $t('global.validation.required') }}
                  </span>
                </div>
              </b-form-group>
            </div>
            <!-- End Command -->
          </b-col>
        </b-row>
      </form>
      <template #modal-footer="{ cancel }">
        <b-row class="mc-modal__filter-footer">
          <b-col sm="12" md="8" class="mc-modal__filter-footer-fav"> </b-col>

          <b-col sm="12" md="4" class="mc-modal__filter-footer-btn">
            <b-button variant="outline-primary" @click="cancel" class="me-3"> Batal </b-button>
            <b-button type="submit" variant="primary" :disabled="isSubmittingCommand" @click="submitDeviceCommand"> {{ isUpdateMode ? 'Update' : 'Simpan' }} </b-button>
          </b-col>
        </b-row>
      </template>
    </b-modal>
  </div>
</template>
